/* ChatHost — renders the /home chat for the shell, and keeps a busy chat
   mounted out of sight while the person is on another page, so a long job
   carries on (the rules are in chatHostState.ts).

   The /home route renders <ChatRequest>, which takes HomeChat's own props and
   hands them here. <ChatHostProvider> wraps the shell; <ChatHostView>, placed
   in the scene area beside the route outlet, renders the instances. Each is
   keyed by its id in one list, so moving it on or off screen never remounts
   it. */

import {
  createContext,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { SceneErrorBoundary } from './SceneErrorBoundary';
import {
  chatHostReducer,
  EMPTY_HOST,
  type ChatHostEvent,
  type ChatHostState,
} from './chatHostState';

const HomeChat = lazy(() => import('@/scenes/HomeChat').then((m) => ({ default: m.HomeChat })));

/** The props HomeChatRoute hands the chat (HomeChat's, less the host's own). */
export interface ChatRequestProps {
  resetKey: number;
  startNew: boolean;
  initialThreadId: string | null;
  initialDraft: string;
  onThreadChange?: (threadId: string | null) => void;
  onDraftSent?: () => void;
}

interface HostApi {
  dispatch: Dispatch<ChatHostEvent>;
  /** The /home route's latest handlers (it re-renders with the address). */
  handlers: MutableRefObject<ChatRequestProps | null>;
  /** The instance on screen now, for callbacks made between renders. */
  shown: MutableRefObject<number | null>;
}

const HostApiContext = createContext<HostApi | null>(null);
const HostStateContext = createContext<ChatHostState>(EMPTY_HOST);

export function ChatHostProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(chatHostReducer, EMPTY_HOST);
  const handlers = useRef<ChatRequestProps | null>(null);
  // Set by ChatHostView, which knows the address.
  const shown = useRef<number | null>(null);
  const api = useMemo<HostApi>(() => ({ dispatch, handlers, shown }), []);
  return (
    <HostApiContext.Provider value={api}>
      <HostStateContext.Provider value={state}>{children}</HostStateContext.Provider>
    </HostApiContext.Provider>
  );
}

/** /home's element: the request for the chat, which <ChatHostView> renders.
 *  Renders nothing itself. */
export function ChatRequest(props: ChatRequestProps) {
  const host = useContext(HostApiContext);
  const { resetKey, startNew, initialThreadId, initialDraft } = props;
  useLayoutEffect(() => {
    if (host) host.handlers.current = props;
  });
  useEffect(() => {
    host?.dispatch({
      type: 'route',
      req: { resetKey, startNew, threadId: initialThreadId, draft: initialDraft },
    });
  }, [host, resetKey, startNew, initialThreadId, initialDraft]);
  useEffect(
    () => () => {
      if (!host) return;
      host.handlers.current = null;
      host.dispatch({ type: 'leave' });
    },
    [host],
  );
  return null;
}

/** The chat instances, in the shell's scene area: the one on screen shown,
 *  any other (busy) one kept out of sight. */
export function ChatHostView({ goHome }: { goHome: () => void }) {
  const host = useContext(HostApiContext);
  const state = useContext(HostStateContext);
  // Out of sight the moment the address leaves /home, in the navigation's own
  // render: the route's request is withdrawn only once the next page has
  // loaded, and until then a busy chat would stay on screen over it.
  const location = useLocation();
  const onHome = location.pathname === '/home';
  const visible = onHome ? state.shown : null;
  if (host) host.shown.current = visible;
  // A chat brought back on screen puts its conversation back in the address
  // (its own [threadId] effect ran while it was out of sight, or before).
  const visibleThread = state.instances.find((i) => i.id === visible)?.threadId ?? null;
  useEffect(() => {
    if (visible !== null && visibleThread) host?.handlers.current?.onThreadChange?.(visibleThread);
  }, [host, visible, visibleThread]);
  if (!host) return null;
  const { dispatch, handlers, shown: shownRef } = host;
  return (
    <>
      {state.instances.map((inst) => {
        const shown = inst.id === visible;
        return (
          <div
            key={inst.id}
            className="chat-host-slot"
            data-chat-instance={inst.id}
            style={shown ? undefined : { display: 'none' }}
            aria-hidden={shown ? undefined : true}
          >
            {/* Reset on navigation, as the route outlet's boundary is. */}
            <SceneErrorBoundary resetKey={`${inst.id}:${location.key}`} onNavigateHome={goHome}>
              <Suspense fallback={null}>
                <HomeChat
                  startNew={inst.req.startNew}
                  initialThreadId={inst.req.threadId}
                  initialDraft={inst.req.draft}
                  background={!shown}
                  othersBusy={state.instances.some((o) => o.id !== inst.id && o.busy)}
                  onThreadChange={(id) => {
                    if (id) dispatch({ type: 'thread', id: inst.id, threadId: id });
                    // Only the chat on screen moves the address.
                    if (shownRef.current === inst.id) handlers.current?.onThreadChange?.(id);
                  }}
                  onDraftSent={() => {
                    if (shownRef.current === inst.id) handlers.current?.onDraftSent?.();
                  }}
                  onActivity={({ busy, threadId }) =>
                    dispatch({ type: 'activity', id: inst.id, busy, threadId })
                  }
                />
              </Suspense>
            </SceneErrorBoundary>
          </div>
        );
      })}
      <style>{`
        .chat-host-slot {
          flex: 1; min-width: 0; min-height: 0;
          display: flex; flex-direction: column; overflow: hidden;
        }
      `}</style>
    </>
  );
}
